import { useMemo } from 'react';
import { InputAdormantHandlerMap, PoiFormState, AllPointOfInterestTemplate } from '@/types';
import { UseTemplateFieldsHookType } from '../hooks/useTemplateFields';
import { TemplateFieldRenderer } from '../shared/TemplateFieldRenderer';
import { Maybe, TemplateFieldTypeEnum, UnitsEnum, UnitSystemEnum } from '@/__generated__/graphql';
import {
  convertInchesToMeters,
  convertMillimetersToMeters,
  localiseMeasurement,
} from '@/utils/unitSystem';
import { unitSystem as unitSystemState } from '@/components/Analysis/state';
import { refreshFields, UNCERTAIN_PROFILE_LABEL, UNCERTAIN_REASON_FIELD_NAME } from '@/constants';
import { useFeatureFlag } from '@/hooks';
import { useAtomValue } from 'jotai';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';

type Props = {
  template: AllPointOfInterestTemplate[0] | undefined;
  dataHook: UseTemplateFieldsHookType;
  formState: PoiFormState | undefined;
  inputAdormantHandlerMap: InputAdormantHandlerMap;
  allDisabled?: boolean;
  handleChangeScCategory?: (argument0: string) => void;
  handleChangeControlledField?: (argument0: string, argument1: string) => void;
};

const filteredFiledNames = new Set(['Comments', UNCERTAIN_REASON_FIELD_NAME]);
const formatValueWithUnit = new Set(['Distance from scanner']);

export const TemplateFieldsForm = ({
  template,
  dataHook,
  formState,
  inputAdormantHandlerMap,
  allDisabled,
  handleChangeControlledField = () => {},
}: Props) => {
  const { state, handleChange, blister } = dataHook;
  const isBetaUser = useFeatureFlag('beta-user');
  const unitSystem = useAtomValue(unitSystemState);
  const isEvaluatingFields = useAtomValue(poiState.isEvaluatingFields);

  const fields = useMemo(
    () =>
      (template?.fields || []).filter(
        ({ name, isHidden, isReadOnly }) =>
          !isHidden && !filteredFiledNames.has(name) && !isReadOnly
      ),
    [template?.fields]
  );

  const formatValue = (
    value: string | undefined,
    unit: Maybe<UnitsEnum> | undefined,
    fieldName: string,
    isReadOnly: boolean
  ) => {
    if (!isReadOnly || !value || !unit || !formatValueWithUnit.has(fieldName)) return value;
    return (
      localiseMeasurement({
        unitSystem,
        sourceUnit: unit,
        value: Number(value),
        roundToDecimal: 2,
        showUnitLabel: true,
      }) ?? value
    );
  };

  return (
    <>
      {fields.map((field) => (
        <TemplateFieldRenderer
          inputAdormantHandlerMap={inputAdormantHandlerMap}
          key={`${field.id}${refreshFields.has(field.key ?? '') ? '-' + state[field.id] : ''}}`}
          isEditing
          templateField={field}
          onChange={(value) => {
            handleChange(field.id, value);
            if (isBetaUser && !!field.detailFieldKey) {
              handleChangeControlledField(field.id, value);
            }
          }}
          value={formatValue(state[field.id], field.unit, field.name, field.isReadOnly)}
          disabled={allDisabled || field.isReadOnly || isEvaluatingFields}
        />
      ))}
      {formState === 'Verify' && blister && (
        <>
          <TemplateFieldRenderer
            inputAdormantHandlerMap={inputAdormantHandlerMap}
            key={'verified-max-height'}
            isEditing
            templateField={{
              name: `Max Height ${unitSystem === UnitSystemEnum.Imperial ? ' (inch)' : ' (mm)'}`,
              id: 'verified-max-height',
              isReadOnly: false,
              isRequired: false,
              options: [],
              type: TemplateFieldTypeEnum.Input,
              serverSide: false,
            }}
            onChange={(value) => {
              if (Number.isNaN(value)) return;
              const valueInMeters =
                unitSystem === UnitSystemEnum.Imperial
                  ? convertInchesToMeters(Number(value))
                  : convertMillimetersToMeters(Number(value));

              if (valueInMeters) handleChange('verified-max-height', valueInMeters.toString());
            }}
            value={
              localiseMeasurement({
                unitSystem,
                value: Number(blister?.verifiedMaxHeight) || blister?.maxHeight || undefined,
                sourceUnit: UnitsEnum.Meter,
                displayAsUnit: UnitsEnum.Millimeter,
                roundToDecimal: 3,
                fallbackLabel: UNCERTAIN_PROFILE_LABEL,
              }) ?? UNCERTAIN_PROFILE_LABEL
            }
            disabled={allDisabled}
          />
          <TemplateFieldRenderer
            inputAdormantHandlerMap={inputAdormantHandlerMap}
            key={'verified-thickness'}
            isEditing
            templateField={{
              name: `Residual Thickness ${
                unitSystem === UnitSystemEnum.Imperial ? ' (inch)' : ' (mm)'
              }`,
              id: 'verified-thickness',
              isReadOnly: false,
              isRequired: false,
              options: [],
              type: TemplateFieldTypeEnum.Input,
              serverSide: false,
            }}
            onChange={(value) => {
              if (Number.isNaN(value)) return;
              const valueInMeters =
                unitSystem === UnitSystemEnum.Imperial
                  ? convertInchesToMeters(Number(value))
                  : convertMillimetersToMeters(Number(value));

              if (valueInMeters) handleChange('verified-thickness', valueInMeters.toString());
            }}
            value={
              localiseMeasurement({
                unitSystem,
                value:
                  Number(blister?.verifiedResidualThickness) || blister?.residualThickness?.value,
              }) ?? UNCERTAIN_PROFILE_LABEL
            }
            disabled={allDisabled}
          />
        </>
      )}
    </>
  );
};
